import React from "react";
import { AccordionProps } from "../../components/shared/Accordion/Accordions";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { HelpFAQAnswerWrapper } from "./Help.styles";

const LINE_BREAK = <br />;

export const helpAccordions = (): AccordionProps[] => [
  {
    title: (
      <>
        Why choose Unity Complete<sup>&reg;</sup>?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> is the only cell-free DNA test that
              provides comprehensive genetic insights from a single maternal
              blood draw. Unlike other tests, it simultaneously assesses:
            </>
          ),
          size: "medium",
        })}
        {LINE_BREAK}
        <ul>
          <li>
            {GetTypographyContent({
              content: "Maternal carrier status for recessive conditions",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: "Fetal risk for recessive genetic disorders",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: "Aneuploidies",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: "Fetal antigens",
              size: "medium",
            })}
          </li>
        </ul>
        {GetTypographyContent({
          content: (
            <>
              By eliminating the need for partner samples and multiple tests,
              Unity Complete<sup>&reg;</sup>:
            </>
          ),
          size: "medium",
        })}
        {LINE_BREAK}
        <ul>
          <li>
            {GetTypographyContent({
              content: "Reduces emotional stress for patients",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: "Provides faster, more comprehensive results",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content:
                "Delivers critical genetic information before the amniocentesis window",
              size: "medium",
            })}
          </li>
        </ul>
      </HelpFAQAnswerWrapper>
    ),
    key: "howToGetTesting",
  },
  {
    title: (
      <>
        What does Unity Complete<sup>&reg;</sup> offer?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> provides comprehensive genetic
              screening from a single maternal blood draw, offering extensive
              insights for expectant parents. The test includes:
            </>
          ),
          size: "medium",
        })}
        {LINE_BREAK}
        {GetTypographyContent({
          content: " Key differentiators include:",
          size: "medium",
        })}
        <ul>
          <li>
            {GetTypographyContent({
              content: "Screening Coverage:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Up to 14 conditions",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content: "Includes ACOG-recommended conditions such as:",
                  size: "medium",
                })}
                <ul>
                  <li>
                    {GetTypographyContent({
                      content: "Cystic fibrosis (CF)",
                      size: "medium",
                    })}
                  </li>
                  <li>
                    {GetTypographyContent({
                      content: "Spinal muscular atrophy (SMA)",
                      size: "medium",
                    })}
                  </li>
                  <li>
                    {GetTypographyContent({
                      content: "Alpha and beta thalassemia",
                      size: "medium",
                    })}
                  </li>
                  <li>
                    {GetTypographyContent({
                      content: "Sickle cell disease",
                      size: "medium",
                    })}
                  </li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            {GetTypographyContent({
              content:
                "Automatic fetal screening via cell-free DNA (cfDNA) for carriers, including:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Condition-specific risk assessment",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content: (
                    <>
                      Fetal sex determination for X-linked conditions (Fragile X
                      syndrome and DMD-Associated Dystrophinopathies)
                      {LINE_BREAK}
                      (note: fetal CGG length analysis for fragile X syndrome is
                      not performed)
                    </>
                  ),
                  size: "medium",
                })}
              </li>
            </ul>
          </li>
          <li>
            {GetTypographyContent({
              content: "Common genetic condition screening, including:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Aneuploidies",
                  size: "medium",
                })}
                :
                <ul>
                  <li>
                    {GetTypographyContent({
                      content: "Trisomy 21 (Down syndrome)",
                      size: "medium",
                    })}
                  </li>
                  <li>
                    {GetTypographyContent({
                      content: "Trisomy 18 (Edwards syndrome)",
                      size: "medium",
                    })}
                  </li>
                  <li>
                    {GetTypographyContent({
                      content: "Trisomy 13 (Patau syndrome)",
                      size: "medium",
                    })}
                  </li>
                </ul>
              </li>
              <li>
                {GetTypographyContent({
                  content: "Sex chromosome abnormalities",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content: "22q11.2 microdeletion syndrome",
                  size: "medium",
                })}
              </li>
            </ul>
          </li>
          <li>
            {GetTypographyContent({
              content: "Additional fetal characteristics:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Fetal sex determination",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content: "RhD status for Rh negative mothers",
                  size: "medium",
                })}
              </li>
            </ul>
          </li>
        </ul>
      </HelpFAQAnswerWrapper>
    ),
    key: "isCovered",
  },
  {
    title: "How is your test different from traditional carrier screening?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Unity Fetal Risk™ Screen revolutionizes prenatal screening by combining comprehensive carrier testing and cell-free DNA (cfDNA) analysis for recessive conditions. Unlike traditional approaches, our test provides a unique, tailored fetal risk assessment directly from a single maternal sample, eliminating the need for additional partner testing or blood draws.",
          size: "medium",
        })}
        {LINE_BREAK}
        {GetTypographyContent({
          content: "Key differentiators include:",
          size: "medium",
        })}
        {LINE_BREAK}
        {LINE_BREAK}
        <ul>
          <li>
            {GetTypographyContent({
              content: "Screening Coverage:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Up to 14 conditions",
                  size: "medium",
                })}
                <ul>
                  <li>
                    {GetTypographyContent({
                      content: "Includes ACOG-recommended conditions such as:",
                      size: "medium",
                    })}
                    <ul>
                      <li>
                        {GetTypographyContent({
                          content: "Cystic fibrosis (CF)",
                          size: "medium",
                        })}
                      </li>
                      <li>
                        {GetTypographyContent({
                          content: "Spinal muscular atrophy (SMA)",
                          size: "medium",
                        })}
                      </li>
                      <li>
                        {GetTypographyContent({
                          content: "Alpha and beta thalassemia",
                          size: "medium",
                        })}
                      </li>
                      <li>
                        {GetTypographyContent({
                          content: "Sickle cell disease",
                          size: "medium",
                        })}
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                {GetTypographyContent({
                  content:
                    "Screens for additional conditions prevalent in Ashkenazi Jewish populations and pan-ethnic populations",
                  size: "medium",
                })}
              </li>
            </ul>
          </li>
          <li>
            {GetTypographyContent({
              content: "Unity Fetal Risk Screen focuses on diseases that are:",
              size: "medium",
            })}
            <ul>
              <li>
                {GetTypographyContent({
                  content: "Known to be severe",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content: "Prenatally diagnosable",
                  size: "medium",
                })}
              </li>
              <li>
                {GetTypographyContent({
                  content:
                    "Capable of improving life expectancy and quality through early detection",
                  size: "medium",
                })}
              </li>
            </ul>
          </li>
        </ul>
      </HelpFAQAnswerWrapper>
    ),

    key: "testDifferent",
  },
  {
    title: "What is the turnaround time?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> uses advanced screening
              technologies to deliver precise fetal risk for common genetic
              conditions and chromosomal conditions. The Unity Aneuploidy Screen
              results are typically returned within seven days of lab receipt,
              while most Unity Fetal Risk Screen results are provided within 14
              days from lab receipt.{" "}
            </>
          ),
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "turnaroundTime",
  },
  {
    title: (
      <>
        How does Unity Complete<sup>&reg;</sup> provide information on fetal
        status for single-gene disorders?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> uses an innovative technology
              called Quantitative Counting Templates™ (QCTs™), developed by
              BillionToOne. This revolutionary approach combines next-generation
              sequencing with advanced molecular quantification, allowing
              precise identification and measurement of genetic mutations
              affecting the pregnancy. By not only detecting mutations but also
              quantifying the number of mutation-containing molecules, Unity
              Complete<sup>&reg;</sup> provides unprecedented insight into fetal
              genetic status.
            </>
          ),
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "fetalStatusSGD",
  },
  {
    title:
      "How was Unity Fetal Risk Screen (carrier screening plus cfDNA for recessive conditions) validated?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Unity Fetal Risk Screen is performed by BillionToOne, a CLIA licensed laboratory, and has undergone rigorous validation using both preclinical and clinical samples in compliance with strict regulatory standards. The test has been substantiated through multiple published analytical validation studies and a comprehensive NIH-supported clinical study examining cfDNA results for recessive conditions. This study, which analyzed over 100 clinical samples, demonstrated 100% concordance with newborn screening results. For more detailed information, interested parties can contact BillionToOne directly at +1(650) 460-2551 or  ",
          size: "medium",
        })}
        {GetTypographyContent({
          content: "view our robust publication history.",
          size: "medium",
          link: "https://www.unityscreen.com/publications",
          textColor: "greenMedium",
          externalLink: true,
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityValidation",
  },
  {
    title: "Is Unity for every patient?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> is designed as a comprehensive
              first-line screening solution for pregnant patients, following the
              standard-of-care guidelines established by ACOG. Beyond standard
              screening, our test offers an expanded 14 gene Unity Fetal Risk
              Screen panel that addresses genetic conditions more prevalent in
              minority populations, including pan-ethnic and Ashkenazi Jewish
              communities.{` BillionToOne's `}mission is to provide equal access
              to high-quality genetic testing, ensuring that every pregnancy can
              receive thorough and personalized genetic screening regardless of
              their background.
            </>
          ),
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "everyPatient",
  },
  {
    title:
      "How does Unity Aneuploidy™ Screen compare with other tests for chromosomal conditions such as Down syndrome?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Unity Aneuploidy™ Screen delivers the high sensitivity and specificity for chromosomal conditions that providers expect from non-invasive prenatal testing (NIPT).  Recognized by ACOG as the most accurate screening method, NIPT significantly reduces false positives and false negatives compared to traditional maternal serum screening. BillionToOne's innovative Spike In's technology provides enhanced clarity and ensures confidence in test results.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "downSyndrome",
  },
  {
    title: (
      <>
        Can Unity Complete<sup>&reg;</sup> be performed on twin pregnancies?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Complete<sup>&reg;</sup> delivers insights for twin
              pregnancies via aneuploidy screening, fetal sex determination,
              zygosity determination, and fetal RhD status. This screening also
              provides maternal carrier screening status, however, cell-free DNA
              (cfDNA) screening for recessive conditions will not be performed
              in twin or higher order multiplet pregnancies.
            </>
          ),
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "twinPregnancies",
  },
  {
    title: (
      <>
        How can I order Unity Complete<sup>&reg;</sup>?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: "Please email us at  ",
          size: "medium",
        })}
        {GetTypographyContent({
          content: "support@unityscreen.com",
          size: "medium",
          isEmail: true,
        })}
        {GetTypographyContent({
          content: " or call us at, ",
          size: "medium",
        })}
        {GetTypographyContent({
          content: "+1(650) 460-2551",
          size: "medium",
          isPhone: true,
        })}
        {GetTypographyContent({
          content: " to set up an account and",
          size: "medium",
        })}
        {GetTypographyContent({
          content: " have kits shipped to you.",
          size: "medium",
          link: "https://www.unityscreen.com/order-a-test-kit",
          textColor: "greenMedium",
          externalLink: true,
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "moreQuestions",
  },
  {
    title: "How early can the testing be performed during pregnancy?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Patients must be at least 8 weeks gestation for aneuploidy in singleton pregnancies or at least 9 weeks in twin pregnancies. Single gene NIPT requires at least 9 weeks gestation. Carrier screening alone is available prior to or during pregnancy at any time.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "howEarlyPregnancy",
  },
  {
    title: (
      <>
        Is Unity Complete<sup>&reg;</sup> available for international markets?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Yes, we accept specimens directly from international laboratories. ",
          size: "medium",
        })}
        {GetTypographyContent({
          content: "Please contact  ",
          size: "medium",
        })}
        {GetTypographyContent({
          content: "support@unityscreen.com",
          size: "medium",
          isEmail: true,
        })}
        {GetTypographyContent({
          content: " for more details.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "internationalMarkets",
  },
];

const getUnityConfirmRapidCvsFootnote = (): React.ReactElement =>
  GetTypographyContent({
    content:
      "*Unity Confirm and rapid CVS both analyze fetal-derived trophoblast cells. Unity Confirm isolates individual cells via whole genome sequencing, which is performed on each cell separately, whereas rapid CVS is often performed via FISH. While rapid CVS may analyze more cells, WGS generates more data per cell. In both rapid CVS and fetal cell capture, mosaicism cannot be excluded. No medical decisions should be made based on Unity Confirm alone. Clinical correlation is necessary",
    size: "xsmall",
  });

export const unityConfirmAccordions = (): AccordionProps[] => [
  {
    title: (
      <>
        What is Unity Confirm<sup>TM</sup>?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            'Unity Confirm is a non-invasive, confirmatory test available exclusively through Unity Aneuploidy Screen for patients who receive a "high-risk" or "detected" Unity Aneuploidy Screen result*. Unity Confirm uses whole genome sequencing (WGS) of intact circulating fetal cells captured from a maternal blood sample to confirm initial screening results.',
          size: "medium",
        })}
        {LINE_BREAK}
        {LINE_BREAK}
        {getUnityConfirmRapidCvsFootnote()}
      </HelpFAQAnswerWrapper>
    ),
    key: "whatIsUnityConfirm",
  },
  {
    title: (
      <>
        How is Unity Confirm<sup>TM</sup> different from Unity Aneuploidy Screen
        or traditional NIPT?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Traditional aneuploidy screening analyzes cfDNA fragments from
              maternal blood to give a fetal risk. Although highly accurate,
              invasive diagnostic testing, such as a CVS or amniocentesis, is
              typically recommended to confirm high-risk results. Unity Confirm
              <sup>TM</sup> is another option that captures intact fetal cells -
              delivering rapid CVS-like insights - without the procedural risk
              to confirm high-risk Unity Aneuploidy results*.
            </>
          ),
          size: "medium",
        })}
        {LINE_BREAK}
        {LINE_BREAK}
        {getUnityConfirmRapidCvsFootnote()}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmDifference",
  },
  {
    title: (
      <>
        What technology does Unity Confirm<sup>TM</sup> use?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content: (
            <>
              Unity Confirm uses Fetal Cell Capture<sup>TM</sup> technology that
              allows isolation of intact circulating fetal cells from maternal
              blood. Unity Confirm closely mirrors the biology of rapid CVS*,
              without the procedural risks of an invasive procedure.
            </>
          ),
          size: "medium",
        })}
        {LINE_BREAK}
        {LINE_BREAK}
        {getUnityConfirmRapidCvsFootnote()}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmTechnology",
  },
  {
    title: (
      <>
        Who is eligible for Unity Confirm<sup>TM</sup>?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            'Unity Confirm is offered to a specific subset of patients in the U.S. who have received a "detected" or "high-risk" aneuploidy result from the Unity Aneuploidy Screen. To qualify for this confirmatory testing, patients must also satisfy these requirements:',
          size: "medium",
        })}
        <ul>
          <li>
            {GetTypographyContent({
              content:
                "Must be willing and available to have a second blood sample collected.",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content:
                "The blood draw must be performed no later than 15 weeks and 6 days of gestation.",
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content:
                'The patient must not have received a "detected" result for Monosomy X.',
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content:
                "Testing is restricted to singleton pregnancies and is not available for twins, vanishing twins, surrogates, or gestational carriers.",
              size: "medium",
            })}
          </li>
        </ul>
        {GetTypographyContent({
          content:
            "For patients that are eligible for Unity Confirm, they will be clearly identified in the portal. Those that do not meet eligibility criteria will not have the option to order Confirm made available.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmEligibility",
  },
  {
    title: (
      <>
        How can I order Unity Confirm<sup>TM</sup> and what are the sample
        requirements?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "At this time, Unity Confirm orders can be placed on this portal. Patients that are eligible will be clearly highlighted in the portal. An attestation will need to be signed prior to placing the order.",
          size: "medium",
        })}
        {LINE_BREAK}
        {LINE_BREAK}
        {GetTypographyContent({
          content:
            "While standard rejection criteria apply (such as incomplete patient data), Unity Confirm has unique requirements to ensure sample viability:",
          size: "medium",
        })}
        <ul>
          <li>
            {GetTypographyContent({
              content: (
                <>
                  <strong>Tube Volume:</strong> The order requires{" "}
                  <strong>4 filled Streck tubes</strong> (approximately 40mL of
                  blood) to ensure a sufficient number of viable cells can be
                  captured.
                </>
              ),
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: (
                <>
                  <strong>Collection Window:</strong> A second blood sample must
                  be drawn by <strong>15 weeks 6 days&apos; gestation.</strong>
                </>
              ),
              size: "medium",
            })}
          </li>
          <li>
            {GetTypographyContent({
              content: (
                <>
                  <strong>Lab Transit:</strong> The laboratory must receive the
                  sample within <strong>6 days</strong> of the collection date.
                </>
              ),
              size: "medium",
            })}
          </li>
        </ul>
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmOrdering",
  },
  {
    title: "What is the expected turnaround time for results?",
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "Results for Unity Confirm are typically delivered within approximately 2 weeks. Reports are accessible through the provider portal and, depending on specific clinic configurations, may also be delivered via EMR or fax.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmTurnaroundTime",
  },
  {
    title: (
      <>
        Can Unity Confirm<sup>TM</sup> be ordered for non-Unity patients?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "No. Due to the extremely labor-intensive process and limited capacity, Unity Confirm is available exclusively for patients who used the Unity Aneuploidy Screen as their primary screening method.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmNonUnityPatients",
  },
  {
    title: (
      <>
        Is Unity Confirm<sup>TM</sup> available for international markets?
      </>
    ),
    content: (
      <HelpFAQAnswerWrapper>
        {GetTypographyContent({
          content:
            "At this time, Unity Confirm is only available for order in the United States.",
          size: "medium",
        })}
      </HelpFAQAnswerWrapper>
    ),
    key: "unityConfirmInternationalMarkets",
  },
];
